import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, InputBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { usePromotionFlow } from '../../hooks/usePromotionFlow';
import { setCodeSentAt, setEnv, setOriginalQuery, setPackageToken, setPhone } from '../../store/mainSlice';
import { useDispatch } from 'react-redux';
import FlowLayout from '../FlowLayout';
import Loading from '../Loading/Loading';
import Splash from '../Splash/Splash';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const Home: React.FC = () => {
    const { t } = useTranslation();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { validatePackage, sendCode } = usePromotionFlow();
    const { isLoading, isFail, flowToken, packageToken, packageInfo, phone } = useAppSelector((state) => state.main);
    const [phoneError, setPhoneError] = React.useState('');
    const [noToken, setNoToken] = React.useState(false);
    const validated = React.useRef(false);

    React.useEffect(() => {
        if (validated.current) return;
        validated.current = true;
        if (flowToken && packageInfo) return;
        const envParam = searchParams.get('ENV');
        if (envParam) {
            if (envParam === 'DEV') dispatch(setEnv('DEV'));
            else if (envParam === 'STAGE') dispatch(setEnv('STAGE'));
            else dispatch(setEnv('PROD'));
        }
        const query = searchParams.toString();
        if (query) dispatch(setOriginalQuery(query));
        const token = searchParams.get('token') ?? packageToken;
        if (!token) {
            setNoToken(true);
            return;
        }
        dispatch(setPackageToken(token));
        validatePackage(token).then((result) => {
            if (!result) navigate('/error');
        });
    }, []);

    if (noToken) return <Splash />;

    const onSubmitPhone = async () => {
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 9) {
            setPhoneError(t('home.phoneError'));
            return;
        }
        setPhoneError('');
        const result = await sendCode(flowToken, phone);
        if (result) {
            dispatch(setCodeSentAt(Date.now()));
            navigate('/otp');
        } else {
            navigate('/error');
        }
    };

    if (isLoading && !packageInfo) return <Loading />;
    if (isFail || !packageInfo) return null;

    return (
        <FlowLayout>
            {/* h1 */}
            <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 27 : 25, fontWeight: 700, lineHeight: 1.25, color: theme4dTokens.text, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '50px' }) }}>
                {packageInfo.promoTitle}
            </Typography>

            {/* lead */}
            {packageInfo.description && (
                <Typography sx={{ fontSize: isDesktop ? 17 : 15, lineHeight: 1.65, color: theme4dTokens.muted, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                    {packageInfo.description}
                </Typography>
            )}

            {/* benefitList */}
            {packageInfo.notes.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', my: '2px', ...(isDesktop && { mt: '16px' }) }}>
                    {packageInfo.notes.map((note) => (
                        <Box key={note} sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 23,
                                    height: 23,
                                    borderRadius: '999px',
                                    bgcolor: theme4dTokens.accentSoft,
                                    color: theme4dTokens.accent,
                                    flexShrink: 0,
                                }}
                            >
                                <CheckIcon sx={{ fontSize: 15 }} />
                            </Box>
                            <Typography sx={{ fontSize: isDesktop ? 16 : 14, color: theme4dTokens.text, lineHeight: 1.4, fontWeight: 600, wordBreak: 'break-word' }}>
                                {note}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* mergedSpacer: label + input + error */}
            <Box sx={{ mt: '4px', ...(isDesktop && { mt: '20px' }) }}>
                <Typography sx={{ fontSize: isDesktop ? 16 : 14, fontWeight: 600, color: theme4dTokens.text, mb: '16px', wordBreak: 'break-word' }}>
                    {t('home.phoneLabel')}
                </Typography>
                <InputBase
                    type="tel"
                    placeholder={t('home.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => dispatch(setPhone(e.target.value))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSubmitPhone();
                    }}
                    inputProps={{ maxLength: 15 }}
                    fullWidth
                    sx={{
                        bgcolor: theme4dTokens.surface,
                        border: `1.5px solid ${phoneError ? '#dc2626' : theme4dTokens.border}`,
                        borderRadius: theme4dTokens.radius,
                        px: '14px',
                        py: '14px',
                        fontSize: isDesktop ? 19 : 17,
                        color: theme4dTokens.text,
                        textAlign: 'right',
                        direction: 'rtl',
                    }}
                />
                {phoneError && (
                    <Typography sx={{ color: '#dc2626', fontSize: isDesktop ? 15 : 13, mt: '8px', fontWeight: 600, wordBreak: 'break-word' }}>
                        {phoneError}
                    </Typography>
                )}
            </Box>

            {/* btnStick */}
            <Button
                variant="contained"
                fullWidth
                disableElevation
                disabled={isLoading}
                onClick={onSubmitPhone}
                sx={{
                    fontFamily: theme4dTokens.headFont,
                    bgcolor: theme4dTokens.accent,
                    color: theme4dTokens.accentText,
                    borderRadius: theme4dTokens.radius,
                    py: '15px',
                    fontSize: isDesktop ? 18 : 16,
                    fontWeight: 700,
                    mt: 'auto',
                    wordBreak: 'break-word',
                    '&:hover': { bgcolor: theme4dTokens.accent, filter: 'brightness(1.07)' },
                }}
            >
                {t('home.continue')}
            </Button>

            {/* note */}
            <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, color: theme4dTokens.muted, textAlign: 'center', lineHeight: 1.5, m: 0, wordBreak: 'break-word' }}>
                {t('home.note')}
            </Typography>
        </FlowLayout>
    );
};

export default Home;
